import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Button,
} from '@mui/material';
import ApplePayButton from '../../ApplePayButton';
import { useApplePay } from '../../../contexts/ApplePayContext';
import { GooglePayButton } from '../../GooglePayButton';
import { PaymentMethod } from '../types';
import { SessionInformation } from '../../../hooks/useStepHandler';
import { Site } from '../../../../generated/graphql';
import { useDurationHumananizer } from '../../../hooks/useDurationHumananizer';
import { ClipLoader } from 'react-spinners';
import { t } from 'i18next';

interface IProps {
  sessionInformation: SessionInformation;
  email: string;
  site: Site;
  paymentMethod: string;
  amount: number;
  processPaymentCallback: (
    site: Site | null,
    sessionInformation: SessionInformation
  ) => Promise<void>;
  paymentLinkLoading: boolean;
}
export const Payment = ({
  sessionInformation,
  site,
  email,
  paymentMethod,
  amount,
  processPaymentCallback,
  paymentLinkLoading,
}: IProps) => {
  const { isApplePayAvailable, handleApplePayClick } = useApplePay();
  const { vrm, duration, selectedFee, phone } = sessionInformation;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const durationText = useDurationHumananizer(duration);

  return (
    <>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          {t('paymentSummary')}
        </Typography>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>{t('location')}:</Typography>
          <Typography fontWeight={500}>{site.name}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>{t('vehicleRegistration')}:</Typography>
          <Typography fontWeight={500}>{vrm}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>{t('duration')}:</Typography>
          <Typography fontWeight={500}>
            {selectedFee === 'MONTHLY'
              ? t('monthlyTime')
              : selectedFee === 'WEEKLY'
              ? t('weeklyTime')
              : durationText}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>{t('feeType')}:</Typography>
          <Typography fontWeight={500}>
            {selectedFee === 'MONTHLY'
              ? t('monthlyFee')
              : selectedFee === 'WEEKLY'
              ? t('weeklyFee')
              : t('hourlyFee')}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>{t('paymentMethod')}:</Typography>
          <Typography fontWeight={500}>{paymentMethod}</Typography>
        </Box>
        {email && paymentMethod !== PaymentMethod.Card && (
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography fontWeight={600}>{t('email')}:</Typography>
            <Typography fontWeight={500}>{email}</Typography>
          </Box>
        )}
        {phone && (
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography fontWeight={600}>{t('phone')}:</Typography>
            <Typography fontWeight={500}>{phone}</Typography>
          </Box>
        )}
        <Divider sx={{ mb: 2 }} />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {t('totalAmount')}:
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            $ {Number(amount).toFixed(2)}
          </Typography>
        </Box>
      </Paper>
      <Box
        display="inline-block"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        sx={{ width: '100%' }}
      >
        {isApplePayAvailable && paymentMethod === PaymentMethod.Apple && (
          <ApplePayButton
            type="pay"
            disabled={false}
            style={{ height: '50px', width: '100%' }}
            onClick={handleApplePayClick(
              amount,
              site,
              sessionInformation,
              setError
            )}
          />
        )}
        {paymentMethod === PaymentMethod.Google && (
          <GooglePayButton
            amount={amount}
            site={site}
            sessionInformation={sessionInformation}
            setError={setError}
            locale="en"
            disabled={loading}
            setGooglePayLoading={setLoading}
          />
        )}
        {paymentMethod === PaymentMethod.Card && (
          <Button
            variant="contained"
            color="primary"
            sx={{ width: '100%' }}
            disabled={paymentLinkLoading}
            onClick={() => processPaymentCallback(site, sessionInformation)}
          >
            {paymentLinkLoading ? (
              <CircularProgress
                size={40}
                sx={{ mr: 2, mt: 1 }}
                color="inherit"
              />
            ) : (
              t('pay')
            )}
          </Button>
        )}
        {loading && (
          <Typography variant="body1" color="textSecondary" mt={2} mb={2}>
            <ClipLoader color="white" />
            {t('pleaseWaitProcessingPayment')}
          </Typography>
        )}
        {paymentLinkLoading && (
          <Typography variant="body1" color="textSecondary" mt={2} mb={2}>
            <ClipLoader color="white" />
            {t('pleaseWaitRedirectingToPaymentPage')}
          </Typography>
        )}
        {error && (
          <Typography variant="body1" color="error" mt={2} mb={2}>
            {error}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        align="center"
      >
        {t('paymentInformationSecure')}
        <br />
        {email && t('paymentInformationSecureHelpText')}
      </Typography>
    </>
  );
};
